export interface RequestCache<K, V> {
    /** Cached value, or the request already running for `key`, or a new request. */
    get: (key: K) => Promise<V>;
    /** Cached value without triggering a request. */
    peek: (key: K) => V | undefined;
}

/**
 * Caches async results by key and shares a request that is still running, so
 * many callers asking for the same key trigger a single request. Failed
 * requests are not cached and the next call retries.
 */
export const createRequestCache = <K, V>(load: (key: K) => Promise<V>): RequestCache<K, V> => {
    const values = new Map<K, V>();
    const pending = new Map<K, Promise<V>>();

    const get = (key: K): Promise<V> => {
        if (values.has(key)) return Promise.resolve(values.get(key) as V);

        let request = pending.get(key);
        if (!request) {
            request = load(key)
                .then(value => {
                    values.set(key, value);
                    return value;
                })
                .finally(() => pending.delete(key));
            pending.set(key, request);
        }
        return request;
    };

    return { get, peek: key => values.get(key) };
};
